<template>
  <div class="wrapper relative">
    <!-- BAR VARIANT -->
    <div v-if="props.variant === 'BAR'">
      <div
        class="full-range absolute left-0 top-0 w-full rounded-full bg-theme-muted"
        :class="barHeight[props.thickness]"
      ></div>

      <div
        class="full-range absolute left-0 top-0 rounded-full bg-theme-3"
        :class="barHeight[props.thickness]"
        :style="{ width: currentProgress }"
      ></div>
    </div>

    <!-- RING VARIANT -->
    <div v-if="props.variant === 'RING'">
      <div class="progress-circle__container" :class="disabled && 'disabled'">
        <span class="progress-circle__percent">{{ current }}%</span>

        <svg class="progress-circle" viewBox="0 0 106 106">
          <g stroke="none" stroke-width="1" fill="none" fill-rule="evenodd">
            <g transform="translate(-17, -17)">
              <circle
                stroke="#cdd1da"
                :stroke-width="ringStrokeWidth[props.thickness]"
                fill-rule="nonzero"
                cx="70"
                cy="70"
                r="50"
              />

              <path
                class="progress-circle__path"
                :stroke-dasharray="currentProgress"
                d="M70,120 C97.6142375,120 120,97.6142375 120,70 C120,42.3857625 97.6142375,20 70,20 C42.3857625,20 20,42.3857625 20,70 C20,97.6142375 42.3857625,120 70,120 Z"
                stroke="#0c1b33"
                :stroke-width="ringStrokeWidth[props.thickness]"
                fill-rule="nonzero"
                transform="translate(70, 70) rotate(-125) translate(-70, -70)"
              />
            </g>
          </g>
        </svg>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { PropType, computed } from "vue";
type Variant = "RING" | "BAR";

type Thickness = "thin" | "medium" | "thick";

const props = defineProps({
  max: { type: Number, default: 100 },
  min: { type: Number, default: 0 },
  current: { type: Number, required: true },
  variant: { type: String as PropType<Variant>, default: "BAR" },
  thickness: { type: String as PropType<Thickness>, default: "medium" },
  disabled: { type: Boolean, default: false },
});

const ringStrokeWidth: Record<Thickness, number> = {
  thin: 2,
  medium: 5,
  thick: 10,
};

const barHeight: Record<Thickness, string> = {
  thin: "h-[2px]",
  medium: "h-2",
  thick: "h-4",
};

const currentProgress = computed(() => {
  if (props.variant === "RING") {
    return (props.current / 100) * 100 * Math.PI + ",9999";
  }

  const rawProgress = (props.current - props.min) / (props.max - props.min);
  // if 0, set progress to 0, else make it a minimum of 3 so the UI looks good
  const progressPercentage = props.current === 0 ? 0 : Math.max(3, rawProgress * 100);

  return progressPercentage + "%";
});
</script>

<style scoped lang="scss">
.disabled {
  @apply text-theme-disabled-e;
}

.progress-circle {
  max-width: 100px;
  max-height: 100px;
  width: 100%;
  transform: scaleX(-1) rotate(-55deg);
  &__percent {
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
  }
  &__container {
    display: inline-block;
    position: relative;
  }
  &__path {
    transition: 0.5s ease-in-out all;
  }
}
</style>
